import React, { useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { UI, cardSx, dateShort, finite } from './operationalUi';
import { previousRunChange, previousRunReason, previousRunValue } from './previousRunUi';
import type { MetricDefinition, PreviousRunMetric, PreviousRunWindow, PreviousRuns } from './types';

interface Props {
  previousRuns?: PreviousRuns;
  definitions: MetricDefinition[];
  variant?: 'portfolio' | 'campaign';
  action?: React.ReactNode;
  onPrevious?: (id: string) => void;
  onExport?: () => void;
  onClearObservation?: () => void;
}
const portfolioMeasures = ['margin_per_day', 'packages_per_day', 'views_to_packages'];
const shortLabels = {
  margin_per_day: 'Margin / day',
  packages_per_day: 'Packages / day',
  views_to_packages: 'Views → packages',
};
const compactTable = {
  tableLayout: 'fixed',
  '& .MuiTableCell-root': { fontSize: 13, px: 0.75, py: 0.2, borderColor: UI.line, lineHeight: 1.3 },
  '& .MuiTableCell-head': { fontSize: 12, bgcolor: UI.header, color: UI.white, fontWeight: 500, whiteSpace: 'nowrap' },
};
function coverageText(window: PreviousRunWindow | null | undefined) {
  if (!window) return 'No recorded window';
  const c = window.coverage;
  return `Impressions ${c.impressions}/${c.calendarDays} · Views ${c.views}/${c.calendarDays} · Sales ${c.sales}/${c.calendarDays}`;
}
function Change({
  metric,
  unit,
  relative = true,
}: {
  metric: Parameters<typeof previousRunChange>[0];
  unit: string;
  relative?: boolean;
}) {
  const delta = previousRunChange(metric, unit);
  return (
    <Box>
      <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{delta.absolute}</Typography>
      {relative && delta.relative && !['percent', 'ratio'].includes(unit) && (
        <Typography sx={{ fontSize: 12, color: UI.muted }}>{delta.relative}</Typography>
      )}
    </Box>
  );
}
export default function PreviousRunCard({
  previousRuns,
  definitions,
  variant = 'portfolio',
  action,
  onPrevious,
  onExport,
  onClearObservation,
}: Props) {
  const [details, setDetails] = useState(false),
    campaign = variant === 'campaign',
    row = previousRuns?.selected,
    summary = previousRuns?.summary;
  const measures = campaign
    ? definitions
    : portfolioMeasures.map((id) => definitions.find((d) => d.id === id)).filter((d): d is MetricDefinition => !!d);
  const paired = campaign ? row?.metrics : summary?.metrics,
    scope = previousRuns?.scope;
  const openPrior = () => {
    if (row?.priorId && onPrevious) onPrevious(row.priorId);
  };
  return (
    <Box
      data-testid={campaign ? 'flash-previous-run-campaign' : 'flash-previous-run-portfolio'}
      sx={{
        ...cardSx,
        p: 1.5,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        gap: campaign ? 0.75 : 0.35,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        sx={{ flexShrink: 0, flexWrap: 'wrap' }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.teal, letterSpacing: 0.5 }}>
          CURRENT V PREVIOUS FLASH
        </Typography>
        {action}
      </Stack>
      {!previousRuns ? (
        <Typography sx={{ fontSize: 13, color: UI.muted, py: 2 }}>
          Previous-run evidence is unavailable for this snapshot.
        </Typography>
      ) : campaign && !row ? (
        <Typography sx={{ fontSize: 13, color: UI.muted, py: 2 }}>
          Choose a campaign to compare it with its previous Flash run.
        </Typography>
      ) : (
        <>
          {campaign ? (
            <Box sx={{ flexShrink: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    title={row?.priorName || undefined}
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row?.priorId ? row.priorName || 'Previous run' : 'No verified previous run in this history'}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: UI.muted }}>
                    {row?.prior
                      ? `${dateShort(row.prior.startDate)}–${dateShort(row.prior.endDate)} ${row.prior.startDate.slice(0, 4)}`
                      : `History starts ${dateShort(scope?.historyStart)} ${scope?.historyStart.slice(0, 4) || ''}`}
                  </Typography>
                </Box>
                {row?.priorId && onPrevious && (
                  <Button size="small" onClick={openPrior} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                    Open previous run
                  </Button>
                )}
              </Stack>
              {row?.available && row?.matchedDays > 0 ? (
                <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.35 }}>
                  First {row.matchedDays} launch days in both runs{row.truncated ? ' · capped by the shorter run' : ''}
                  {row.matchBasis === 'offer' ? ' · same offer; property mapping missing' : ''}
                  {scope?.market && scope.market !== 'All website markets'
                    ? ` · ${scope.market} website market`
                    : ' · all website markets'}
                  .
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 12, color: UI.muted, mt: 0.35 }}>
                  {previousRunReason(row?.reason)}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 12, color: UI.muted, flexShrink: 0 }}>
              Same launch age · {scope.minimumSummaryDays}+ days
            </Typography>
          )}
          {onClearObservation && (
            <Box sx={{ flexShrink: 0 }}>
              {!campaign && (
                <Typography sx={{ fontSize: 13 }}>
                  {previousRunReason('absolute_observation_window_incompatible')}
                </Typography>
              )}
              <Button size="small" onClick={onClearObservation} sx={{ py: 0 }}>
                Clear observation dates
              </Button>
            </Box>
          )}
          {!onClearObservation && (
            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table
                size="small"
                stickyHeader
                sx={{ ...compactTable, minWidth: campaign ? 470 : 420 }}
                aria-label={campaign ? 'Campaign versus previous Flash run' : 'Portfolio versus previous Flash runs'}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: campaign ? '38%' : '35%' }}>
                      {campaign ? 'Measure' : 'Measure · pairs'}
                    </TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">Previous</TableCell>
                    <TableCell align="right" sx={{ width: campaign ? '22%' : '22%' }}>
                      Change
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {measures.map((d) => {
                    const metric = paired?.[d.id],
                      available =
                        !!metric &&
                        finite(metric.current) &&
                        finite(metric.prior) &&
                        (!campaign || (metric as PreviousRunMetric).available),
                      reason = campaign
                        ? (metric as PreviousRunMetric)?.reason
                        : scope?.market && ['impressions_per_day', 'impressions_to_views'].includes(d.id)
                          ? 'Not available by website market'
                          : null;
                    return (
                      <TableRow key={d.id}>
                        <TableCell>
                          <Box component="span" sx={{ fontSize: 13 }}>
                            {campaign ? d.label : shortLabels[d.id] || d.label}
                          </Box>
                          {!campaign && (
                            <Box component="span" sx={{ fontSize: 12, color: UI.muted, ml: 0.5 }}>
                              · {summary.metrics[d.id]?.pairCount || 0}/{summary.totalCampaigns}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={!available ? previousRunReason(reason) : ''}>
                            <Box component="span">{previousRunValue(metric?.current, d.unit)}</Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">{previousRunValue(metric?.prior, d.unit)}</TableCell>
                        <TableCell align="right">
                          <Change metric={available ? metric : undefined} unit={d.unit} relative={campaign} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ flexShrink: 0, flexWrap: 'wrap' }}
          >
            <Typography sx={{ fontSize: 12, color: UI.muted, lineHeight: 1.25 }}>
              {campaign
                ? !row?.available
                  ? `History from ${dateShort(scope.historyStart)} ${scope.historyStart.slice(0, 4)} · facts through ${dateShort(scope.cutoff)}.`
                  : row?.prior
                    ? `Recorded days · current ${coverageText(row.current)}; previous ${coverageText(row.prior)}.`
                    : `No earlier run verified since ${scope.historyStart}.`
                : ''}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {onExport && (
                <Button
                  size="small"
                  onClick={onExport}
                  sx={{ whiteSpace: 'nowrap', fontSize: 12, minWidth: 0, py: 0, minHeight: 24 }}
                >
                  Export
                </Button>
              )}
              <Button
                size="small"
                onClick={() => setDetails(true)}
                sx={{ whiteSpace: 'nowrap', fontSize: 12, minWidth: 0, py: 0, minHeight: 24 }}
              >
                Details
              </Button>
            </Stack>
          </Stack>
        </>
      )}
      <Dialog open={details} onClose={() => setDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Previous Flash comparison</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.25}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Matching</Typography>
              <Typography sx={{ fontSize: 13 }}>
                Same verified property and campaign type; same offer only where property mapping is missing. Use the
                immediately earlier launch. Overlapping or ambiguous runs are withheld.
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Measured window</Typography>
              <Typography sx={{ fontSize: 13 }}>
                First N launch days in both runs, capped by the shorter campaign. Website market applies to both;
                current campaign filters do not remove its historical run. Absolute observation dates must be cleared.
                Portfolio figures pool valid pairs with seven or more days, per measure.
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: UI.muted }}>
              History starts {scope?.historyStart}; facts through {scope?.cutoff}. A missing previous run does not
              establish that this is a first campaign. Missing source records are not treated as zero. Relative
              percentage changes require a positive previous value.
            </Typography>
            {!campaign && summary && (
              <Typography sx={{ fontSize: 13 }}>
                {summary.matchedCampaigns}/{summary.totalCampaigns} campaigns have paired evidence;{' '}
                {summary.matureMatchedCampaigns} with {scope?.minimumSummaryDays}+ aligned days. Each measure below uses
                its own valid pairs.
              </Typography>
            )}
            {!campaign && summary && Object.keys(summary.reasons).length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>Comparison coverage</Typography>
                {Object.entries(summary.reasons).map(([reason, count]) => (
                  <Typography key={reason} sx={{ fontSize: 13 }}>
                    {count} · {previousRunReason(reason)}
                  </Typography>
                ))}
              </Box>
            )}
            {campaign && row && (
              <>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Recorded source coverage</Typography>
                <Table size="small" sx={compactTable}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Run</TableCell>
                      <TableCell>Measured window</TableCell>
                      <TableCell>Recorded days</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[
                      ['Current', row.current],
                      ['Previous', row.prior],
                    ].map(([label, window]: [string, PreviousRunWindow]) => (
                      <TableRow key={label}>
                        <TableCell>{label}</TableCell>
                        <TableCell>{window ? `${window.windowStart}–${window.windowEnd}` : 'Unavailable'}</TableCell>
                        <TableCell>{coverageText(window)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
            <Table size="small" sx={compactTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Measure</TableCell>
                  <TableCell>{campaign ? 'Availability' : 'Pairs / campaign-days'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {definitions.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.label}</TableCell>
                    <TableCell>
                      {campaign
                        ? row?.metrics[d.id]?.available
                          ? 'Available'
                          : previousRunReason(row?.metrics[d.id]?.reason)
                        : `${summary?.metrics[d.id]?.pairCount || 0} pairs · ${summary?.metrics[d.id]?.pairedDays || 0} paired days`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
